import { useMemo, useState } from "react";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/outline";
import highlightJson from "../../utils/highlightJson";

interface QuoteCalculationJsonCardProps {
  collectedCalculationJson?: string | null;
  dropOffCalculationJson?: string | null;
}

interface ParsedCalculationJson {
  formatted: string | null;
  raw: string | null;
  isValid: boolean;
}

const parseCalculationJson = (value?: string | null): ParsedCalculationJson => {
  if (!value) return { formatted: null, raw: null, isValid: false };

  try {
    return {
      formatted: JSON.stringify(JSON.parse(value), null, 2),
      raw: null,
      isValid: true,
    };
  } catch {
    return {
      formatted: null,
      raw: value,
      isValid: false,
    };
  }
};

const JsonBox = ({
  title,
  value,
}: {
  title: string;
  value: ParsedCalculationJson;
}) => {
  if (!value.formatted && !value.raw) return null;

  return (
    <div className="rounded-md border border-gray-200 bg-gray-50 p-3">
      <h3 className="text-sm font-medium text-gray-700 mb-2">{title}</h3>
      <pre className="text-xs whitespace-pre-wrap break-words max-h-64 overflow-auto bg-white border border-gray-200 rounded p-3 text-gray-800">
        {value.formatted ? highlightJson(value.formatted) : value.raw}
      </pre>
      {!value.isValid && (
        <p className="mt-2 text-xs text-amber-700">
          Invalid JSON string. Showing raw value.
        </p>
      )}
    </div>
  );
};

export default function QuoteCalculationJsonCard({
  collectedCalculationJson,
  dropOffCalculationJson,
}: QuoteCalculationJsonCardProps) {
  const [isOpen, setIsOpen] = useState(false);

  const collectionJson = useMemo(
    () => parseCalculationJson(collectedCalculationJson),
    [collectedCalculationJson]
  );
  const dropOffJson = useMemo(
    () => parseCalculationJson(dropOffCalculationJson),
    [dropOffCalculationJson]
  );

  if (!collectionJson.formatted && !collectionJson.raw && !dropOffJson.formatted && !dropOffJson.raw) {
    return null;
  }

  return (
    <div className="bg-white shadow rounded-lg">
      <button
        type="button"
        className="w-full px-4 py-4 sm:px-6 flex items-center justify-between text-left"
        onClick={() => setIsOpen((prev) => !prev)}
      >
        <h2 className="text-lg font-medium leading-6 text-gray-900">
          Raw Data
        </h2>
        {isOpen ? (
          <ChevronUpIcon className="h-5 w-5 text-gray-600" />
        ) : (
          <ChevronDownIcon className="h-5 w-5 text-gray-600" />
        )}
      </button>

      {isOpen && (
        <div className="border-t border-gray-200 p-4 space-y-4">
          <JsonBox title="Collection" value={collectionJson} />
          <JsonBox title="Drop Off" value={dropOffJson} />
        </div>
      )}
    </div>
  );
}
